/**
 * Marcos (bolinha) e faixas do calendário do cronograma.
 *
 * Módulo PRÓPRIO, sem importar o `react-day-picker`: ele é ESM e o Jest do CRA
 * não carrega uma suíte que o alcance — dentro do componente, esta regra ficaria
 * sem cobertura possível.
 *
 * REGRA (03/09/2026): o PERÍODO DO PROJETO entra **só como faixa**. Ele ganhava
 * também um marco em cada extremo, e a bolinha não significava nada para quem
 * olhava — o início e o fim já são as bordas da própria faixa. Marco aqui quer
 * dizer "acontece neste dia", que é o caso do evento de 1 dia; usá-lo na borda
 * de um intervalo dizia outra coisa.
 */

/**
 * Converte uma data do backend no DIA que ela representa, fixado ao MEIO-DIA
 * local.
 *
 * Por que existe (03/09/2026): o período do projeto é gravado à MEIA-NOITE UTC
 * — `2026-08-20 00:00:00+00`, que em São Paulo (UTC-3) é `19/08 21:00`. Usando
 * o instante cru, o calendário destacava a faixa a partir do dia **19**, um dia
 * antes do que o projeto declara.
 *
 * Duas decisões aqui:
 *
 * 1. O dia é lido em **UTC** (`getUTCDate`), porque é assim que ele foi
 *    gravado: a meia-noite UTC representa o dia 20, não o 19. Os eventos de
 *    cronograma também batem, porque o backend grava com o `+3h` legado
 *    (`2030-05-10 03:00 UTC` → dia 10 em UTC e em São Paulo).
 * 2. A hora é fixada em **12:00**, não 0h. Meio-dia sobra 12 horas de folga
 *    para cada lado, então nenhum fuso entre UTC-11 e UTC+12 empurra a data
 *    para o dia anterior ou seguinte — que é exatamente o que 0h faria.
 *
 * ⚠️ A MESMA função alimenta a lista e o calendário. É o que garante que os
 * dois não possam divergir: era a divergência visível na tela (a lista dizia
 * 20/08 e a faixa começava no 19).
 */
export const toCalendarDay = (value) => {
  if (!value) return null;

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
    12,
    0,
    0,
    0,
  );
};

// Evento de 1 dia = término no mesmo DIA do início (ou ausente, nas linhas
// legadas). A comparação é entre dias normalizados, não entre instantes: um
// evento gravado com horas diferentes no mesmo dia continua sendo de 1 dia.
export const isSingleDay = (event) => {
  if (!event.finish_date) return true;

  const start = toCalendarDay(event.date);
  const finish = toCalendarDay(event.finish_date);

  return start?.getTime() === finish?.getTime();
};

export const buildCalendarModifiers = (events = [], projectPeriod = null) => {
  // Marcos: o início de todo evento (e o término, quando houver período).
  const marks = events.flatMap((event) =>
    isSingleDay(event)
      ? [toCalendarDay(event.date)]
      : [toCalendarDay(event.date), toCalendarDay(event.finish_date)],
  );

  // Faixas: só os eventos com período de verdade — evento de 1 dia fica no
  // marco, que é a "exibição simplificada" pedida no PDF.
  const ranges = events
    .filter((event) => !isSingleDay(event))
    .map((event) => ({
      from: toCalendarDay(event.date),
      to: toCalendarDay(event.finish_date),
    }));

  // O período do projeto (não é evento: vem do formulário base) entra apenas
  // aqui — sem marco nos extremos.
  if (projectPeriod)
    ranges.push({
      from: toCalendarDay(projectPeriod.periodStart),
      to: toCalendarDay(projectPeriod.periodFinish),
    });

  return { marks, ranges };
};
